import ReactDOM from 'react-dom/client'
import reportWebVitals from './reportWebVitals'
import './fonts/NunitoSans.ttf'
import './i18n'
import { ModalRoot } from './components/ModalPortal/ModalRoot'
import { ToastContainer } from 'react-toastify'
import { AuthContextProvider } from './contexts/AuthContext'
import Backend from './Backend'
import { PluginContextProvider } from './contexts/PluginContext'
import { OnboardingContextProvider } from './contexts/OnboardingContext'
import { toSnakeCase } from './helpers/stringHelper'
import OnboardingForm from './compositions/OnboardingForm'

const plugin_id: string | undefined = process.env.REACT_APP_PLUGIN_ID
const rootIdName = `${toSnakeCase(plugin_id)}_root`

const customizerId = `${(plugin_id ?? 'plugin').trim().toLowerCase().replace(/\s+/g, '-')}`

const mode: string | undefined = process.env.REACT_APP_MODE
const isAdminPage = window.location.href.includes('/admin/plugin') || window.location.href.includes(customizerId)
const context: string | undefined =
    mode === 'production'
        ? isAdminPage ? 'admin' : 'customizer'
        : isAdminPage ? 'admin' : process.env.REACT_APP_CONTEXT

const adminRootElement = document.getElementById(rootIdName)
const frontendContainer = document.getElementById('zr-onboarding-form-frontend')

const isAdmin = adminRootElement !== null || (isAdminPage && !frontendContainer)

if (isAdmin && adminRootElement) {
    const root = ReactDOM.createRoot(adminRootElement)
root.render(
    <PluginContextProvider>
        <AuthContextProvider>
            <Backend />
        </AuthContextProvider>
        <ModalRoot />
        <ToastContainer />
    </PluginContextProvider>
)
}

if (!isAdmin && frontendContainer) {
    let frontendRoot: ReturnType<typeof ReactDOM.createRoot> | null = null
    let mountedContainer: HTMLElement | null = null

    const onboardingTree = (
        <PluginContextProvider>
            <OnboardingContextProvider>
                <OnboardingForm className="zr-onboarding-form-wrapper" />
                <ModalRoot />
                <ToastContainer />
            </OnboardingContextProvider>
        </PluginContextProvider>
    )

    // Hard ceiling on recovery attempts. Without it, a script that keeps clearing
    // the container would make us keep remounting, which is how the tab used to
    // grow without bound.
    const MAX_REMOUNTS = 5
    let remounts = 0

    const mount = (container: HTMLElement) => {
        // Always release the previous root first: calling createRoot() twice on a
        // container leaks the entire tree and re-runs every startup request.
        if (frontendRoot) {
            try {
                frontendRoot.unmount()
            } catch {
                // The old container was already detached or cleared by another
                // script, so React cannot remove its own nodes. Dropping the root
                // reference is all the cleanup we can do here.
            }
            frontendRoot = null
        }

        mountedContainer = container
        frontendRoot = ReactDOM.createRoot(container)
        frontendRoot.render(onboardingTree)
    }

    const renderOnboardingForm = () => {
        const container = document.getElementById('zr-onboarding-form-frontend')

        if (!container) {
            return
        }

        if (!frontendRoot) {
            mount(container)
            return
        }

        // Healthy: still mounted into the element that is in the document.
        if (mountedContainer === container && container.isConnected) {
            return
        }

        // The container we mounted into was replaced or removed by another script.
        if (remounts >= MAX_REMOUNTS) {
            return
        }

        remounts++
        mount(container)
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', renderOnboardingForm, { once: true })
    } else {
        renderOnboardingForm()
    }

    // Safety net for the container being replaced or removed by the template or
    // another plugin. Scoped to the container's parent, childList only -- unlike
    // watching all of document.body with subtree:true, DOM churn elsewhere on the
    // page (theme sliders, consent manager, our own renders) cannot trigger it.
    if (frontendContainer.parentElement) {
        const observer = new MutationObserver(() => renderOnboardingForm())
        observer.observe(frontendContainer.parentElement, { childList: true })
        window.addEventListener('pagehide', () => observer.disconnect(), { once: true })
    }
}

reportWebVitals()
